import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import StoryCard from "parts/Cards/StoryCard";
import PrefecturesCard from "parts/Cards/PrefecturesCard";

import JapanMap from "components/JapanMap";

import { useContext, useEffect, useState, useRef } from "react";
import { LanguageContext } from "context";

import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";
import { usePaywall } from "context/PaywallContext"; 

import { getDashboardJapanMap, type PrefectureCompletionResult } from "@shared/api/dashboard";
import { getUserInfos } from "@shared/api/userInfo";

import {
  getStory,
  getStoryHistory,
  getProgress,
  getComment,
  saveProgress,
  saveComment,
  type StoryInfo,
  type ProgressInfo,
  type CommentInfo,
} from "@shared/api/story";

import defaultImage from "/images/default-card.png";

import { voicePlayAudioStreamAuto } from "@shared/api/voice";

const formatPrefectureId = (id: number) => id.toString().padStart(2, "0");

function Story() {
  const { t } = useContext(LanguageContext) as { t: (key: string) => string };
  const { refresh, session } = useSessionContext();
  const { showNotification } = useNotification();
  const { openPaywall } = usePaywall();

  const [loading, setLoading] = useState(false);

  const [story, setStory] = useState<StoryInfo>(null);
  const [japanMapStats, setJapanMap] = useState<PrefectureCompletionResult>(null);
  const [comment, setComment] = useState<CommentInfo>(null);
  const [progresss, setProgress] = useState<ProgressInfo>(null);
  const [userPlan, setUserPlan] = useState<"Free" | "Pro">("Free");

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [storyHistory, setStoryHistory] = useState<StoryInfo[]>([]);
  const [selectedPastStory, setSelectedPastStory] = useState<StoryInfo | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        if (!session?.access_token) return;
        const resStory = await getStory(session.access_token);
        setStory(resStory);
      } catch (err) {
        showNotification({ icon: "error", title: t("Common.error"), message: t("Common.get-error") }, 2000);
      }
    };
    const fetchComment = async () => {
      try {
        if (!session?.access_token) return;
        const comment = await getComment(session.access_token);
        setComment(comment);
      } catch (err) {
        showNotification({ icon: "error", title: t("Common.error"), message: t("Common.get-error") }, 2000);
      }
    };
    const fetchProgress = async () => {
      try {
        if (!session?.access_token) return;
        const progress = await getProgress(session.access_token);
        setProgress(progress);
      } catch (err) {
        showNotification({ icon: "error", title: t("Common.error"), message: t("Common.get-error") }, 2000);
      }
    };
    const fetchJapanMap = async () => {
      try {
        if (!session?.access_token) return;
        const japanMap = await getDashboardJapanMap(session.access_token);
        setJapanMap(japanMap);
      } catch (err) {
        showNotification({ icon: "error", title: t("Common.error"), message: t("Common.get-error") }, 2000);
      }
    };

    const fetchUserPlan = async () => {
      try {
        if (!session?.access_token) return;

        const userInfo = await getUserInfos(session.access_token);
        let isPro = false;

        if (userInfo.plan_type === "Pro" && userInfo.subscription_expires_at) {
          const expiresAt = new Date(userInfo.subscription_expires_at).getTime();
          const now = Date.now();
          
          if (expiresAt > now) {
            isPro = true;
          }
        }

        setUserPlan(isPro ? "Pro" : "Free");
      } catch (err) {
        console.error("Plan取得エラー:", err);
        setUserPlan("Free");
      }
    };

    fetchStory();
    fetchComment();
    fetchProgress();
    fetchJapanMap();
    fetchUserPlan();
  }, [session?.access_token]);

  useEffect(() => {
    if (selectedPastStory && dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [selectedPastStory]);

  const handleThumbFeedback = async (type: "comment" | "prefecture", isUp: boolean) => {
    if (!session?.access_token) return;
    try {
      if (type === "comment" && comment) {
        let newUp = comment.ai_comment_up;
        let newDown = comment.ai_comment_down;

        if (isUp) {
          newUp = !comment.ai_comment_up;
          if (newUp) newDown = false;
        } else {
          newDown = !comment.ai_comment_down;
          if (newDown) newUp = false;
        }

        setComment(prev => prev ? { ...prev, ai_comment_up: newUp, ai_comment_down: newDown } : prev);
        const updated = await saveComment(session.access_token, comment.id, newUp, newDown);
        setComment(prev => prev ? { 
          ...prev, 
          ai_comment_up: updated.ai_comment_up !== undefined ? updated.ai_comment_up : newUp, 
          ai_comment_down: updated.ai_comment_down !== undefined ? updated.ai_comment_down : newDown 
        } : prev);
      }

      if (type === "prefecture" && progresss?.id) {
        let newUp = progresss.thumb_up;
        let newDown = progresss.thumb_down;

        if (isUp) {
          newUp = !progresss.thumb_up;
          if (newUp) newDown = false;
        } else {
          newDown = !progresss.thumb_down;
          if (newDown) newUp = false;
        }

        setProgress(prev => prev ? { ...prev, thumb_up: newUp, thumb_down: newDown } : prev);
        const updated = await saveProgress(session.access_token, progresss.id, newUp, newDown);        
        setProgress(prev => prev ? { 
          ...prev, 
          thumb_up: updated.thumb_up !== undefined ? updated.thumb_up : newUp, 
          thumb_down: updated.thumb_down !== undefined ? updated.thumb_down : newDown 
        } : prev);
      }
    } catch (err) {
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: `${type === "comment" ? t("StoryCard.ai-comment") : t("StoryCard.prefecture")}${isUp ? t("StoryCard.good") : t("StoryCard.bad")}${t("StoryCard.error_update")}`,
      }, 2000);
    }
  };

  const handleVoicePlay = async (comment_type: string, ai_comments_id: number, partner_voice_name: string) => {
    try {
      if (!ai_comments_id || !session?.access_token) return;
      
      setLoading(true);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.load();
        await voicePlayAudioStreamAuto(session.access_token, comment_type, ai_comments_id, partner_voice_name, audioRef.current);
      }
    } catch (err) {
      showNotification({ icon: "error", title: t("Common.error"), message: t("AITalk.error.play") }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHistory = async () => {
    try {
      if (!session?.access_token) return;
      const history = await getStoryHistory(session.access_token);
      setStoryHistory(history);
      setHistoryDialogOpen(true);
    } catch (err) {
      showNotification({ icon: "error", title: t("Common.error"), message: t("StoryTab.history_fetch_error") }, 2000);
    }
  };

  const handleCloseHistory = () => {
    setHistoryDialogOpen(false);
    setSelectedPastStory(null);
  };

  const actionButtons = (comment_type: string, ai_comments_id: number, partner_voice_name: string) => (
    <>
      <Tooltip title={t("DashboardPage.voice-label")} placement="bottom">
        <MDTypography variant="body1" color="info" lineHeight={1} sx={{ cursor: "pointer", mx: 3 }} onClick={() => handleVoicePlay(comment_type, ai_comments_id, partner_voice_name)}>
          <Icon color="inherit">record_voice_over</Icon>
        </MDTypography>
      </Tooltip>
    </>
  );

  const isStoryLocked = userPlan === "Free" && story && story.story_id >= 6;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      {/* コンテンツ領域を覆うための relative ボックス */}
      <MDBox sx={{ position: "relative", minHeight: "80vh" }}>
        <audio ref={audioRef} hidden controls />

        {/* スピナーを重ねる（loading=true のときだけ） */}
        {loading && (
          <Fade in timeout={300}>
            {/* 外側のBox: コンテンツ領域全体を覆う（横幅をサイドバー右側に限定） */}
            <MDBox
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                backgroundColor: "rgba(255, 255, 255, 0.4)", 
              }}
            >
              {/* 内側のBox: 縦位置を画面の中央に固定する */}
              <MDBox
                sx={{
                  position: "sticky",
                  top: "50vh",
                  transform: "translateY(-50%)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="info" />
              </MDBox>
            </MDBox>
          </Fade>
        )}

        {isStoryLocked ? (
          <MDBox 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            p={3} 
            mb={3} 
            sx={{ 
              backgroundColor: "#FFEBEE", 
              border: "1px solid #FFCDD2", 
              borderRadius: "12px",
              mx: { xs: 0, md: 2 } 
            }}
          >
            <MDBox display="flex" alignItems="center" mb={2}>
              <Icon sx={{ color: "#d32f2f", mr: 1 }}>error_outline</Icon>
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                {t("StoryTab.locked_free_msg")}
              </MDTypography>
            </MDBox>
            <MDButton 
              variant="contained" 
              color="info" 
              size="large" 
              onClick={openPaywall} 
              sx={{ 
                borderRadius: "30px", 
                px: 4, 
                py: 1.5,
                fontSize: "16px",
                boxShadow: "0px 4px 10px rgba(26, 115, 232, 0.3)" 
              }}
            >
              {t("menu.upgrade_pro")}
            </MDButton>
          </MDBox>
        ) : (
          <>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12}>
                <MDBox p={3} sx={{ border: "1px solid #ccc", borderRadius: "12px", minHeight: "300px", maxHeight: "300px", overflowY: "auto", backgroundColor: "#fafafa" }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="h5" fontWeight="bold" sx={{ fontFamily: `"Yu Mincho", "Hiragino Mincho ProN", serif`, mr: 2 }}>
                      {story?.title ?? ""}
                    </MDTypography>
                    <Button variant="contained" size="small" onClick={handleOpenHistory} sx={{ whiteSpace: "nowrap", backgroundColor: "#3b82f6", fontSize: "10px", color: "#ffffff", "&:hover": { backgroundColor: "#233043" } }}>
                      {t("StoryTab.view_history")}
                    </Button>
                  </MDBox>
                  <MDTypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {story?.content ?? t("StoryCard.get-story")}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>

            <MDBox py={3}>
              <Grid container spacing={3} alignItems="flex-end">
                <Grid item xs={12} md={6} lg={4}>
                  <StoryCard
                    image={comment?.partner_image_url ?? defaultImage}
                    title={comment?.partner_title?? ""}
                    description={comment?.content ?? ""}
                    action={comment ? actionButtons("storyComment", comment.id, comment.partner_voice_name || "") : undefined}
                    isThumbUp={comment?.ai_comment_up ?? false}
                    isThumbDown={comment?.ai_comment_down ?? false}
                    onThumbUp={comment ? () => handleThumbFeedback("comment", true) : undefined}
                    onThumbDown={comment ? () => handleThumbFeedback("comment", false) : undefined}
                    disabledThumb={!comment || Array.isArray(comment)}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4} sx={{ display: "flex", alignItems: "stretch" }}>
                  <PrefecturesCard
                    image={progresss?.prefecture_id ? `/images/prefectures/JP-${formatPrefectureId(progresss.prefecture_id)}.svg` : defaultImage}
                    title={progresss?.name ?? t("StoryCard.in-prefecture")}
                    description={progresss?.history_culture ?? ""}
                    touristAttractions={progresss?.recommended_attractions ?? ""}
                    specialtyProducts={progresss?.specialty_products ?? ""}
                    action={progresss ? actionButtons("prefecturesComment", progresss.prefecture_id, comment?.partner_voice_name || "") : undefined}
                    isThumbUp={progresss?.thumb_up ?? false}
                    isThumbDown={progresss?.thumb_down ?? false}
                    onThumbUp={progresss ? () => handleThumbFeedback("prefecture", true) : undefined}
                    onThumbDown={progresss ? () => handleThumbFeedback("prefecture", false) : undefined}
                    disabledThumb={!progresss || Array.isArray(progresss)}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </>
        )}

        <MDBox py={3}>
          <Grid container>
            {japanMapStats && (
              <JapanMap completedPrefectures={japanMapStats.completed_prefectures} inProgressPrefectures={japanMapStats.in_progress_prefectures} />
            )}
          </Grid>
        </MDBox>

        <Footer />
      </MDBox> {/* relative ボックスの終了位置 */}

      <Dialog open={historyDialogOpen} onClose={handleCloseHistory} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", fontFamily: `"Yu Mincho", "Hiragino Mincho ProN", serif` }}>
          {selectedPastStory ? selectedPastStory.title : t("StoryTab.past_stories")}
        </DialogTitle>
        <DialogContent dividers ref={dialogContentRef}>
          {selectedPastStory ? (
            <MDBox p={1}>
              <MDTypography variant="body2" color="dark" sx={{ lineHeight: 1.8, letterSpacing: '0.02em', fontSize: '0.9rem', color: '#000000' }}>
                {selectedPastStory.content.replace(/。/g, "。\n").split("\n").filter((line: string) => line.trim() !== "").map((line: string, index: number) => (
                  <span key={index} style={{ display: "block", marginBottom: "16px" }}>{line}</span>
                ))}
              </MDTypography>
            </MDBox>
          ) : (
            <List>
              {storyHistory.map((hist, index) => (
                <ListItem disablePadding key={hist.story_id || index}>
                  <ListItemButton onClick={() => setSelectedPastStory(hist)}>
                    <ListItemText primary={<MDTypography variant="body2" sx={{ fontSize: '0.9rem', color: '#000000', letterSpacing: '0.02em' }}>{hist.title}</MDTypography>} />
                  </ListItemButton>
                </ListItem>
              ))}
              {storyHistory.length === 0 && (
                <MDTypography variant="body2" sx={{ p: 2 }}>{t("StoryTab.no_past_stories")}</MDTypography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPastStory && (
            <MDButton onClick={() => setSelectedPastStory(null)} color="dark" variant="outlined" sx={{ mr: 1, color: '#000000' }}>
              {t("StoryTab.back_to_list")}
            </MDButton>
          )}
          <MDButton color="dark" variant="gradient" onClick={handleCloseHistory}>
            {t("StoryCard.close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Story;